<template>
	<AppLayout
		:title="trans('menu.tools.accreditations')"
		:subtitle="trans('tools.accreditations.show.title')"
		:return-url="route('tools.accreditations.scan.index')"
		:return-label="trans('menu.tools.accreditations')"
	>
		<Card :title="enrollment.event.title" :subtitle="enrollment.event.colleges.map((c:College) => c.name).join(', ')">
			<template #badges>
				<Badge v-if="enrollment.accredited_at" :color="Color.Green" active>{{ trans('general.accreditation.present') }}</Badge>
				<Badge v-else :color="Color.Red" active>{{ trans('general.accreditation.absent') }}</Badge>
				<Badge>{{ formatNumber(parseInt(user.id_number), 0, '', '.') }}</Badge>
			</template>
			<p class="text-2xl">{{ user.full_name }}</p>
			<template #footer>
				<SecondaryButton :color="Color.Blue" @click.stop="toggle">
					<i class="fa-solid fa-arrows-rotate mr-2"/>
					{{ trans('tools.accreditations.show.toggle') }}
				</SecondaryButton>
			</template>
		</Card>
		<template v-if="enrollment.accredited_at">
			<p class="text-xl uppercase font-bold">{{ trans('events.activities.title') }}</p>
			<CardGrid>
				<Activity
					v-for="(activity, activityIndex) in enrollment.selected_activities"
					:key="`enrollment-activity-${activity.id}`"
					:activity="activity"
					@ban="banActivity(activityIndex, activity.pivot.id)"
					@accredit="accreditActivity(activityIndex, activity.pivot.id)"
				/>
			</CardGrid>
		</template>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {type User} from '@/Types/User'
import {Color} from '@/Types/Enums/Color'
import {defineProps, ref, type Ref} from 'vue'
import Card from '@/Components/UI/Cards/Card.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import {type College} from '@/Types/Settings/College'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import {type Enrollment} from '@/Types/Events/Enrollment'
import Activity from '@/Components/Tools/Accreditations/Activity.vue'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {formatNumber, showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	user:User,
	enrollment:Enrollment,
}>()

const user:Ref<User> = ref(props.user)
const enrollment:Ref<Enrollment> = ref(props.enrollment)

function toggle():void{
	if(enrollment.value?.accredited_at){
		ban()
	} else {
		accredit()
	}
}

function accredit():void{
	if(enrollment.value){
		showInfo(trans('enrollments.accreditation.accrediting'))
		axios.patch(route('tools.accreditations.enrollments.update', enrollment.value.id), {
			action: 'accredit',
		}).then(() => {
			if(enrollment.value){
				enrollment.value.accredited_at = dayjs().format('YYYY-MM-DD HH:mm:ss')
			}
			showSuccess(trans('enrollments.accreditation.accredited'))
		}).catch(showValidationErrors)
	}
}

function ban():void{
	if(enrollment.value){
		showInfo(trans('enrollments.accreditation.banning'))
		axios.patch(route('tools.accreditations.enrollments.update', enrollment.value.id), {
			action: 'ban',
		}).then(() => {
			if(enrollment.value){
				enrollment.value.accredited_at = null
			}
			showSuccess(trans('enrollments.accreditation.banned'))
		}).catch(showValidationErrors)
	}
}

function accreditActivity(activityIndex:number, enrollmentActivityId:number):void{
	if(enrollment.value){
		showInfo(trans('enrollments.accreditation.accrediting'))
		axios.patch(route('tools.accreditations.activities.update', enrollmentActivityId), {
			action: 'accredit',
		}).then(() => {
			updateActivityByIndex(activityIndex)
			showSuccess(trans('enrollments.accreditation.accredited'))
		}).catch(showValidationErrors)
	}
}

function updateActivityByIndex(activityIndex:number):void{
	if(enrollment.value){
		if(enrollment.value.selected_activities[activityIndex].pivot.accredited_at){
			enrollment.value.selected_activities[activityIndex].pivot.accredited_at = null
		} else {
			enrollment.value.selected_activities[activityIndex].pivot.accredited_at = dayjs().format('YYYY-MM-DD HH:mm:ss')
		}
	}
}

function banActivity(activityIndex:number, selectedPackActivityId:number):void{
	if(enrollment.value){
		showInfo(trans('enrollments.accreditation.banning'))
		axios.patch(route('tools.accreditations.activities.update', selectedPackActivityId), {
			action: 'ban',
		}).then(() => {
			updateActivityByIndex(activityIndex)
			showSuccess(trans('enrollments.accreditation.banned'))
		}).catch(showValidationErrors)
	}
}
</script>
